import { useState } from "react";
import {
  Activity,
  Radio,
  Brain,
  Sparkles,
  ListTodo,
  Bookmark,
  Trophy,
  Battery,
} from "lucide-react";

const tabs = [
  {
    key: "now",
    label: "Now",
    headline: "Your current task, cognitive load, top priorities, and restart tools.",
    items: [
      { icon: ListTodo, t: "Current thread", v: "Finish proposal draft" },
      { icon: Activity, t: "Cognitive load", v: "Elevated — let's simplify" },
      { icon: Sparkles, t: "Try this", v: "Open the doc, write one messy sentence." },
    ],
    accent: "#2563EB",
  },
  {
    key: "signals",
    label: "Signals",
    headline: "A live dashboard of focus signals and system status.",
    items: [
      { icon: Radio, t: "State", v: "Pre-distraction · 38s" },
      { icon: Activity, t: "Tab switches (5m)", v: "12 — slightly elevated" },
      { icon: Sparkles, t: "Suggestion", v: "One tab close + a 90-sec breath." },
    ],
    accent: "#F59E0B",
  },
  {
    key: "memory",
    label: "Memory",
    headline: "Parked thoughts, brain dumps, and session recovery.",
    items: [
      { icon: Bookmark, t: "Parked", v: "3 thoughts saved with their source pages" },
      { icon: Brain, t: "Brain dump", v: "234 words · 2 patterns surfaced" },
      { icon: Sparkles, t: "Last session", v: "Resume Q1 doc + research tabs" },
    ],
    accent: "#7C3AED",
  },
  {
    key: "review",
    label: "Review",
    headline: "Patterns, wins, energy matching, and gentle tuning.",
    items: [
      { icon: Trophy, t: "Wins this week", v: "4 — including \"sent the email\"" },
      { icon: Battery, t: "Energy now", v: "Low-medium · matching tasks shown" },
      { icon: Activity, t: "Pattern", v: "Mornings: focus. Late afternoons: drift." },
    ],
    accent: "#22C55E",
  },
];

export default function Walkthrough() {
  const [active, setActive] = useState("now");
  const current = tabs.find((t) => t.key === active);

  return (
    <section
      id="walkthrough"
      data-testid="walkthrough-section"
      className="px-6 lg:px-10 py-24 lg:py-32"
    >
      <div className="max-w-6xl mx-auto">
        <div className="fade-up max-w-3xl">
          <p className="text-sm tracking-[0.18em] uppercase font-semibold text-[#475569]">
            Side panel walkthrough
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.05]">
            One calm side panel. Four simple spaces.
          </h2>
        </div>

        <div className="mt-12 fade-up">
          <div className="flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`walkthrough-tab-${t.key}`}
                onClick={() => setActive(t.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                  active === t.key
                    ? "bg-[#0F172A] text-[#F8FAFC] border-[#0F172A]"
                    : "bg-white text-[#0F172A] border-[#E2E8F0] hover:border-[#22C55E]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-5">
              <div
                className="h-full rounded-3xl p-8 border border-[#E2E8F0]"
                style={{ background: current.accent + "12" }}
              >
                <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#475569]">
                  {current.label} space
                </p>
                <p className="mt-4 font-display text-2xl sm:text-3xl text-[#0F172A] leading-snug">
                  {current.headline}
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-white border border-[#E2E8F0] rounded-3xl p-6 lg:p-8 shadow-[0_8px_30px_rgba(42,48,43,0.04)]">
                <ul className="divide-y divide-dashed divide-[#E2E8F0]">
                  {current.items.map((it, i) => (
                    <li
                      key={i}
                      className="py-4 first:pt-0 last:pb-0 flex items-start gap-4"
                    >
                      <span
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                        style={{ background: current.accent + "1f" }}
                      >
                        <it.icon
                          className="w-4 h-4"
                          strokeWidth={1.5}
                          style={{ color: current.accent }}
                        />
                      </span>
                      <div>
                        <p className="text-xs tracking-[0.18em] uppercase font-semibold text-[#475569]">
                          {it.t}
                        </p>
                        <p className="font-display text-lg text-[#0F172A] mt-1 leading-snug">
                          {it.v}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
